import {
	Directive,
	ElementRef,
	HostListener,
	Input,
	OnChanges,
	OnDestroy
} from '@angular/core';

import { HlsUtilService } from 'vbrick-player-src/HlsUtil.Service';
import { IVbSource } from 'vbrick-player-src/IVbSource';

const STALL_TIMEOUT_MS: number = 30000;

/**
 * Workaround for detecting stalls during native HLS playback (iOS).
 *
 * The iOS error handling for HLS is not very sophisticated once the video is rolling. For example, if the playlist fetching starts to fail
 * then it just remains stuck in a waiting state.
 *
 * What this directive does is to set a timeout following the waiting event. If it reaches the timeout without being canceled,
 * then it triggers a native error event which in turn will produce the expected behaviors such as reporting of an error and a fall back.
 */
@Directive({
	selector: '[native-hls-stall-detector]',
    standalone: false
})
export class NativeHlsStallDetectorDirective implements OnChanges, OnDestroy {
	@Input() public activeSource: IVbSource;

	private isActive: boolean;
	private waitingTimeoutRef: number;

	constructor(
		private element: ElementRef<HTMLMediaElement>,
		private HlsUtilService: HlsUtilService
	) {}

	public ngOnChanges(): void {
		this.isActive = this.activeSource && this.HlsUtilService.isNativePlayback(this.activeSource);

		this.reset();
	}

	public ngOnDestroy(): void {
		this.reset();
	}

	private reset(): void {
		if (this.waitingTimeoutRef) {
			window.clearTimeout(this.waitingTimeoutRef);
			this.waitingTimeoutRef = null;
		}
	}

	@HostListener('playing')
	private onPlaying(): void {
		this.reset();
	}

	@HostListener('seeked')
	private onSeeked(): void {
		this.reset();
	}

	@HostListener('waiting')
	private onWaiting(): void {
		if (this.isActive) {
			this.reset();

			this.waitingTimeoutRef = window.setTimeout(() => this.onWaitingTimeout(), STALL_TIMEOUT_MS);
		}
	}

	private onWaitingTimeout(): void {
		const videoElement: HTMLMediaElement = this.element.nativeElement;

		// Native events can be unreliable, so perform a safety check on the readyState to short-circuit if it's no longer stalled.
		if (videoElement.readyState >= 2) {
			return;
		}

		videoElement.dispatchEvent(new Event('error'));
	}
}
